import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react';
import { useAuthStore } from '@/features/auth';
import { api } from '../services/api';
import { useSystemSettings } from './SystemSettingsContext';

/**
 * Layout mode options
 */
export type LayoutMode = 'topnav' | 'sidebar';

/**
 * Layout context value
 */
export interface LayoutContextValue {
  /** Current layout mode */
  layoutMode: LayoutMode;

  /** Change layout mode */
  setLayoutMode: (mode: LayoutMode) => Promise<void>;

  /** Sidebar collapsed state (only relevant for sidebar mode) */
  sidebarCollapsed: boolean;

  /** Toggle sidebar collapsed state */
  toggleSidebar: () => void;

  /** Loading state */
  isLoading: boolean;
}

/**
 * Layout context
 */
export const LayoutContext = createContext<LayoutContextValue | undefined>(undefined);

const STORAGE_KEY = 'bodywave-layout-mode';
const SIDEBAR_STORAGE_KEY = 'bodywave-sidebar-collapsed';

/**
 * Get initial layout mode from localStorage
 */
const getInitialLayoutMode = (): LayoutMode => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'sidebar' || stored === 'topnav') {
    return stored as LayoutMode;
  }
  return 'topnav'; // Default
};

/**
 * Get initial sidebar collapsed state from localStorage
 */
const getInitialSidebarCollapsed = (): boolean => {
  const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
  return stored === 'true';
};

export interface LayoutProviderProps {
  children: ReactNode;
}

/**
 * Layout Provider Component
 * Manages layout mode (topnav/sidebar) and sidebar state
 * Syncs with database for authenticated users
 */
export const LayoutProvider = ({ children }: LayoutProviderProps) => {
  const { settings } = useSystemSettings();
  const [layoutMode, setLayoutModeState] = useState<LayoutMode>(getInitialLayoutMode);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(getInitialSidebarCollapsed);
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated } = useAuthStore();

  // Apply system default layout mode when settings load (if user hasn't set a preference)
  useEffect(() => {
    if (settings?.layoutMode) {
      const userPreference = localStorage.getItem(STORAGE_KEY);
      // If no user preference in localStorage, use system default
      if (!userPreference) {
        setLayoutModeState(settings.layoutMode as LayoutMode);
      }
    }
  }, [settings?.layoutMode]);

  // TODO: Database sync for layout preferences (backend endpoint not implemented yet)
  // Layout preferences are currently stored in localStorage only
  // When /api/v1/users/me/layout-preference endpoint is created, uncomment this code:

  // useEffect(() => {
  //   const loadLayoutPreferences = async () => {
  //     if (!isAuthenticated) return;
  //
  //     setIsLoading(true);
  //     try {
  //       const response = await api.get('/users/me/layout-preference');
  //       if (response.data.success) {
  //         const { layoutMode: dbLayoutMode, sidebarCollapsed: dbSidebarCollapsed } = response.data.data;
  //
  //         if (dbLayoutMode && dbLayoutMode !== layoutMode) {
  //           setLayoutModeState(dbLayoutMode);
  //           localStorage.setItem(STORAGE_KEY, dbLayoutMode);
  //         }
  //
  //         if (typeof dbSidebarCollapsed === 'boolean' && dbSidebarCollapsed !== sidebarCollapsed) {
  //           setSidebarCollapsed(dbSidebarCollapsed);
  //           localStorage.setItem(SIDEBAR_STORAGE_KEY, String(dbSidebarCollapsed));
  //         }
  //       }
  //     } catch (error: any) {
  //       console.error('Failed to fetch layout preferences:', error);
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   };
  //
  //   loadLayoutPreferences();
  // }, [isAuthenticated]);

  /**
   * Change layout mode with database sync
   */
  const setLayoutMode = async (mode: LayoutMode): Promise<void> => {
    // Update state and localStorage immediately
    setLayoutModeState(mode);
    localStorage.setItem(STORAGE_KEY, mode);

    // Save to database if authenticated
    if (isAuthenticated) {
      try {
        await api.patch('/users/me/layout-preference', { layoutMode: mode });
      } catch (error) {
        console.error('Failed to save layout mode:', error);
      }
    }
  };

  /**
   * Toggle sidebar collapsed state with database sync
   */
  const toggleSidebar = async () => {
    const newCollapsedState = !sidebarCollapsed;

    // Update state and localStorage immediately
    setSidebarCollapsed(newCollapsedState);
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(newCollapsedState));

    // Save to database if authenticated
    if (isAuthenticated) {
      try {
        await api.patch('/users/me/layout-preference', {
          sidebarCollapsed: newCollapsedState
        });
      } catch (error) {
        console.error('Failed to save sidebar state:', error);
      }
    }
  };

  // Memoize context value
  const contextValue = useMemo<LayoutContextValue>(() => ({
    layoutMode,
    setLayoutMode,
    sidebarCollapsed,
    toggleSidebar,
    isLoading,
  }), [layoutMode, sidebarCollapsed, isLoading]);

  return (
    <LayoutContext.Provider value={contextValue}>
      {children}
    </LayoutContext.Provider>
  );
};

/**
 * Hook to access layout context
 * @throws Error if used outside LayoutProvider
 */
export const useLayout = (): LayoutContextValue => {
  const context = useContext(LayoutContext);

  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }

  return context;
};
